import React from "react";
import { Field, ErrorMessage, useField } from "formik";
import cssModuleClassNames from "classnames/bind";
import formStyles from "../form.module.css";
import FormErrors from "../form-errors";
import cssStyles from "./segmented-control.module.css";
const classNames = cssModuleClassNames.bind(cssStyles);

interface Option {
  label: React.ReactNode;
  value: string;
}

interface RadioGroupProps {
  name: string;
  options: Option[];
  containerClassName?: string;
}

type ControlProps = Option & {
  styles: Record<string, string>;
  name: string;
};

const Control: React.FC<ControlProps> = ({ styles, name, label, value }) => {
  const idName = `hc-${name}-${value}`;
  const [field] = useField(name);
  return (
    <div className={styles.control}>
      <Field
        type="radio"
        id={idName}
        value={value}
        name={name}
        checked={value === field.value}
      />
      <label htmlFor={idName}>{label}</label>
    </div>
  );
};

/**
 * iOS style segment radio control with Formik support
 * Example:
 * ```
 *   <SegmentedControl
 *     name="age"
 *     legend="Applicant Age"
 *     options=[{label: "One", value: 1}, {label: "Two", value: 2}]
 *   />
 * ```
 */
const SegmentedControl: React.FC<RadioGroupProps> = props => (
  <div className={classNames(cssStyles.container, props.containerClassName)}>
    {props.options.map(({ label, value }, key) => (
      <Control key={key} styles={cssStyles} label={label} value={value} name={props.name} />
    ))}
  </div>
);

/**
 * iOS style segment radio control with a label above control along with error handling
 * Example:
 * ```
 *   <Formik>
 *     <SegmentedControlWithLabelError
 *       name="age"
 *       legend="Applicant Age"
 *       options=[{label: "One", value: 1}, {label: "Two", value: 2}]
 *     />
 * ```
 */
export const SegmentedControlWithLabelError: React.FC<{ legend: string } & RadioGroupProps> =
props => {
  const { legend, ...controlProps } = props;
  return (
    <fieldset className={formStyles.inputGroup}>
      <legend className={cssStyles.legend}>
        {legend}
        <ErrorMessage name={props.name} component="span" className={cssStyles.legendError} />
      </legend>
      <FormErrors name={props.name}>
        {errMsg => {
          const classes = classNames({ inputError: !!errMsg });
          return <SegmentedControl containerClassName={classes} {...controlProps} />;
        }}
      </FormErrors>
    </fieldset>
  );
};
